import { HttpClient, HttpParams, HttpHeaders, HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { environment } from "../../../environments/environment";

export enum ApiType {
  RETAIL = "retail",
  INVENTORY = "inventory"
}

interface RequestHeaderValues {
  businessId?: string;
  terminalId?: string;
}

export abstract class ServiceBase {

  protected apiUrl: string;
  protected apiInventoryUrl: string;
  protected terminalId: string | null = null;
  protected businessId: string | null = null;


  constructor(
    protected http: HttpClient,
    protected router: Router
  ) {
    this.apiUrl = this.getApiUrlSync();
    this.apiInventoryUrl = this.getInventoryApiUrlSync();
  }


  protected getTerminalIdValue(): string {
    return sessionStorage.getItem('terminalId') || '';
  }

  protected getBusinessIdValue(): string {
    return sessionStorage.getItem('businessId') || '';
  }

  // 2) Create a helper to build headers with terminalId
  private buildHeaders(headerValues: RequestHeaderValues = {}): HttpHeaders {
    let headers = new HttpHeaders();
    const terminalId = headerValues.terminalId ?? this.getTerminalIdValue();
    const businessId = headerValues.businessId ?? this.getBusinessIdValue();
    if (terminalId) {
      headers = headers.set('X-Terminal-ID', terminalId);
    }
    if (businessId) {
      headers = headers.set('X-Business-ID', businessId);
    }
    return headers;
  }

  // Synchronous API URL assignment logic
  private getApiUrlSync(): string {
    const offlineMode = sessionStorage.getItem('OfflineModeEnabled') === "true";

    if (!offlineMode) {
      return environment.retailApiUrl;
    }

    const isConnected = sessionStorage.getItem('is_connected') === "true";
    if (isConnected) {
      return environment.retailApiUrlLocal;
    }

    this.router.navigate(['/offline-login']);
    throw new Error("No API URL available - user redirected to offline login.");
  }

  private getInventoryApiUrlSync(): string {
    const offlineMode = sessionStorage.getItem('OfflineModeEnabled') === "true";

    if (!offlineMode) {
      return environment.inventoryApiUrl;
    }

    const isConnected = sessionStorage.getItem('is_connected') === "true";
    if (isConnected) {
      return environment.inventoryApiUrlLocal;
    }

    this.router.navigate(['/offline-login']);
    throw new Error("No Inventory API URL available - user redirected to offline login.");
  }

  private getBaseUrl(apiType: ApiType): string {
    return apiType === ApiType.RETAIL ? this.apiUrl : this.apiInventoryUrl;
  }

  // 3) Include the headers in every request

  protected get<T>(endpoint: string, apiType: ApiType, params?: HttpParams, headerValues?: RequestHeaderValues): Observable<T> {
    return this.http.get<T>(
      `${this.getBaseUrl(apiType)}/${endpoint}`, 
      { params, headers: this.buildHeaders(headerValues) }
    ).pipe(catchError(error => this.handleError(error)));
  }

  protected post<T>(endpoint: string, body: any, apiType: ApiType, headerValues?: RequestHeaderValues): Observable<T> {
    return this.http.post<T>(
      `${this.getBaseUrl(apiType)}/${endpoint}`, 
      body, 
      { headers: this.buildHeaders(headerValues) }
    ).pipe(catchError(error => this.handleError(error)));
  }

  protected put<T>(endpoint: string, body: any, apiType: ApiType, headerValues?: RequestHeaderValues): Observable<T> {
    return this.http.put<T>(
      `${this.getBaseUrl(apiType)}/${endpoint}`, 
      body, 
      { headers: this.buildHeaders(headerValues) }
    ).pipe(catchError(error => this.handleError(error)));
  }

  protected delete<T>(endpoint: string, apiType: ApiType, headerValues?: RequestHeaderValues): Observable<T> {
    return this.http.delete<T>(
      `${this.getBaseUrl(apiType)}/${endpoint}`, 
      { headers: this.buildHeaders(headerValues) }
    ).pipe(catchError(error => this.handleError(error)));
  }

  private handleError(error: any): Observable<never> {
    let errorMessage = 'API request failed';
  
    if (error instanceof HttpErrorResponse) {
      const errorText = error.message?.toLowerCase() || '';
  
      // if (errorText.includes('http failure')) {
      //   errorMessage = 'Something went wrong. Please try again.';
      // } else 
        if (typeof error.error === 'string') {
        errorMessage = error.error;
      } else if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
    } else if (error?.message) {
      errorMessage = error.message;
    }
  
    console.error('API Error:', error);
    return throwError(() => new Error(errorMessage));
  }
  
  
}
